import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Alert, { AlertSize, AlertVariant } from '../../atoms/alert/Alert';
import Button, { ButtonSize, ButtonVariant } from '../../atoms/buttons/button/Button';
import { formatDateTime, type Survey, type SurveyStatsData } from './types';
import { SurveyStatusBadge } from './SurveyStatusBadge';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const SKIPPED_COLOR = '#d1d5db';

type Props = {
  survey: Survey;
  stats: SurveyStatsData | null;
  loading: boolean;
  error: boolean;
  onBack: () => void;
};

function downloadCSV(survey: Survey, stats: SurveyStatsData) {
  const rows: (string | number)[][] = [
    ['Thème', 'Date début', 'Date fin', 'Option', 'Type', 'Réponses', '% du total'],
    ...stats.stats.map((s) => [
      survey.theme,
      survey.date_start,
      survey.date_end,
      s.question ?? '',
      s.question_type,
      s.nbResponse,
      stats.total_responses > 0
        ? `${Math.round((Number(s.nbResponse) / stats.total_responses) * 100)}%`
        : '0%',
    ]),
    [
      survey.theme,
      survey.date_start,
      survey.date_end,
      'Sans réponse',
      '-',
      stats.skipped,
      stats.total_responses > 0
        ? `${Math.round((stats.skipped / stats.total_responses) * 100)}%`
        : '0%',
    ],
  ];

  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sondage-${survey.id}-${survey.theme.toLowerCase().replace(/\s+/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function StatCard({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-neutral-200 bg-white p-4">
      <p className="text-2xl font-bold text-neutral-800">{value.toLocaleString('fr-FR')}</p>
      <p className="text-lg text-neutral-500">{label}</p>
    </div>
  );
}

export function SurveyStatsView({ survey, stats, loading, error, onBack }: Props) {
  const answered = stats ? stats.total_responses - stats.skipped : 0;

  const hasAnyResponse = stats && stats.total_responses > 0;

  const showChart =
    stats !== null &&
    hasAnyResponse &&
    stats.stats.some((s) => s.question_type !== 'textArea');

  const pieData = stats
    ? [
        ...stats.stats.map((s, i) => ({
          name: s.question ?? 'N/A',
          value: Number(s.nbResponse),
          color: COLORS[i % COLORS.length],
        })),
        ...(stats.skipped > 0 ? [{ name: 'Sans réponse', value: stats.skipped, color: SKIPPED_COLOR }] : []),
      ].filter((d) => d.value > 0)
    : [];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Button variant={ButtonVariant.GHOST} size={ButtonSize.SM} onClick={onBack}>
          ← Retour
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-neutral-800 truncate">{survey.theme}</h2>
          <p className="text-lg text-neutral-400 truncate">{survey.description}</p>
        </div>
        <SurveyStatusBadge status={survey.status} />
        {stats && (
          <Button
            variant={ButtonVariant.DEFAULT}
            size={ButtonSize.SM}
            onClick={() => downloadCSV(survey, stats)}
          >
            ↓ Exporter CSV
          </Button>
        )}
      </div>

      {loading && (
        <p className="py-12 text-center text-lg text-neutral-400">Chargement des statistiques...</p>
      )}

      {error && (
        <Alert variant={AlertVariant.DESTRUCTIVE} size={AlertSize.SMALL}>
          Impossible de charger les statistiques.
        </Alert>
      )}

      {!loading && !error && stats && (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <StatCard value={stats.total_responses} label="Total interactions" />
            <StatCard value={answered} label="Ont répondu" />
            <StatCard value={stats.skipped} label="Sans réponse" />
          </div>

          {stats.stats.length === 0 && stats.total_responses === 0 && (
            <p className="py-12 text-center text-lg text-neutral-400">Aucune réponse pour l'instant.</p>
          )}

          {stats.stats.length > 0 && (
            <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
              <div className={showChart ? 'flex' : ''}>
                {showChart && (
                  <div className="w-72 shrink-0 border-r border-neutral-100 p-4 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `${Number(value).toLocaleString('fr-FR')} réponses`,
                            name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-lg font-medium text-neutral-500">
                    <div className="flex-1 min-w-0">Option</div>
                    <div className="w-24 shrink-0 text-right">Réponses</div>
                    <div className="w-16 shrink-0 text-right">%</div>
                  </div>

                  {stats.stats.map((stat, i) => {
                    const pct =
                      stats.total_responses > 0
                        ? Math.round((Number(stat.nbResponse) / stats.total_responses) * 100)
                        : 0;
                    return (
                      <div
                        key={stat.id_question}
                        className="flex items-center px-4 py-3 border-b border-neutral-100 last:border-0"
                      >
                        <div className="flex flex-1 min-w-0 items-center gap-2.5">
                          {showChart && (
                            <span
                              className="inline-block size-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: COLORS[i % COLORS.length] }}
                            />
                          )}
                          <span className="text-lg text-neutral-700 truncate">
                            {stat.question ?? '—'}
                          </span>
                        </div>
                        <div className="w-24 shrink-0 text-right text-lg font-medium text-neutral-800">
                          {Number(stat.nbResponse).toLocaleString('fr-FR')}
                        </div>
                        <div className="w-16 shrink-0 text-right text-lg text-neutral-500">
                          {pct}%
                        </div>
                      </div>
                    );
                  })}

                  {stats.skipped > 0 && (
                    <div className="flex items-center px-4 py-3 border-t border-dashed border-neutral-200 bg-neutral-50">
                      <div className="flex flex-1 min-w-0 items-center gap-2.5">
                        {showChart && (
                          <span
                            className="inline-block size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: SKIPPED_COLOR }}
                          />
                        )}
                        <span className="text-lg text-neutral-400 italic">Sans réponse</span>
                      </div>
                      <div className="w-24 shrink-0 text-right text-lg font-medium text-neutral-500">
                        {stats.skipped.toLocaleString('fr-FR')}
                      </div>
                      <div className="w-16 shrink-0 text-right text-lg text-neutral-400">
                        {stats.total_responses > 0
                          ? Math.round((stats.skipped / stats.total_responses) * 100)
                          : 0}%
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-4 py-2.5 border-t border-neutral-100 bg-neutral-50 text-right">
                <span className="text-lg text-neutral-400">
                  Période : {formatDateTime(survey.date_start)} → {formatDateTime(survey.date_end)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
